import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import FeaturedProducts from './components/FeaturedProducts';
import Solutions from './components/Solutions';
import ServicesCards from './components/ServicesCards';
import BlogSection from './components/BlogSection';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WebApplications from './components/Products/Product1';
import MobileApplications from './components/Products/Product2';
import EcommercePlatforms from './components/Products/Product3';
import CRMSystems from './components/Products/Product4';
import ERPSolutions from './components/Products/Product5';
import ProductList from './components/Products/ProductList';
import Career from './components/Career';
import Company from './components/About/Company';
import Team from './components/About/Team';
import Certification from './components/About/Certification';
import PolicyViewer from './components/About/Certification/PolicyViewer';
import Invoice from './components/About/Invoice';
import Cerez from './components/About/Certification/Cerez';
import GizlilikVeri from './components/About/Certification/GizlilikVeri';
import KVKKAydinlatma from './components/About/Certification/KVKKAydinlatma';
import VeriSorumlusuBasvuru from './components/About/Certification/VeriSorumlusuBasvuru';
import GizlilikKullanim from './components/About/Certification/GizlilikKullanim';
import BilgiToplumu from './components/About/Certification/BilgiToplumu';
import Investments from './components/Investments';
import Blog from './components/Blog';
import BlogDetail from './components/Blog/BlogDetail';
import Subsidiaries from './components/About/Subsidiaries';
import Sustainability from './components/About/Sustainability';
import HR from './components/About/HR';
import SocialResponsibility from './components/About/SocialResponsibility';
import BlogPost from './components/About/SocialResponsibility/BlogPost';

const App: React.FC = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const langFromUrl = pathname.startsWith('/en') ? 'en' : 'tr';
  const [language, setLanguage] = useState<'tr' | 'en'>(langFromUrl as 'tr' | 'en');

  // 404 yönlendirmesinin yapıldığı yer hatırlatma* ---->
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      navigate(redirect, { replace: true });
    }
  }, [navigate]);

  const handleSetLanguage = (lang: 'tr' | 'en') => {
    setLanguage(lang);
    if (lang === 'tr') navigate('/tr', { replace: true });
    else navigate('/en', { replace: true });
  };

  useEffect(() => {
    setLanguage(langFromUrl as 'tr' | 'en');
  }, [langFromUrl]);

  // Resimlerde sağ tık (kaydet / yeni sekmede aç) ve sürükleyerek kaydetmeyi engelle
  useEffect(() => {
    const isImage = (t: EventTarget | null) => (t as HTMLElement)?.tagName === 'IMG';
    const handleContextMenu = (e: MouseEvent) => {
      if (isImage(e.target)) e.preventDefault();
    };
    const handleDragStart = (e: DragEvent) => {
      if (isImage(e.target)) e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  // Contact scroll için effect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scroll = params.get('scroll');
    if (scroll === 'contact') {
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          params.delete('scroll');
          navigate({ search: params.toString() }, { replace: true });
        }
      }, 300);
    }
  }, [pathname, search, navigate]);

  return (
    <Routes>
      {/* 404 yönlendirmesinin yapıldığı yer hatırlatma* ----> */}
      <Route path="/" element={<Navigate to="/tr" replace />} />
      <Route path=":lang" element={
        <>
          <Header language={language} setLanguage={handleSetLanguage} />
          <Hero language={language} />
          <FeaturedProducts language={language} />
          <Solutions language={language} />
          <BlogSection language={language} />
          <Contact language={language} />
          <Footer language={language} />
        </>
      } />
      {/* Router yapılandırmaları* ---- */}
      <Route path=":lang/products/web-applications" element={<WebApplications />} />
      <Route path=":lang/products/web-applications/urun" element={<WebApplications />} />
      <Route path=":lang/products/web-applications/urun-lite" element={<WebApplications />} />
      <Route path=":lang/products/erp-solutions" element={<ERPSolutions />} />
      <Route path=":lang/products/crm-systems" element={<CRMSystems />} />
      <Route path=":lang/products/ecommerce-platforms" element={<EcommercePlatforms />} />
      <Route path=":lang/products/mobile-applications" element={<MobileApplications />} />
      <Route path=":lang/products" element={<ProductList />} />
      <Route path=":lang/solutions" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution1" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution1/urun" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution1/urun-lite" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution2" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution2/urun" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution2/urun-lite" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution3" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution3/urun" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution3/urun-lite" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution4" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution4/urun" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution4/urun-lite" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution5" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution5/urun" element={<Solutions language={language} />} />
      <Route path=":lang/solutions/solution5/urun-lite" element={<Solutions language={language} />} />
      <Route path=":lang/services" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service1" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service1/urun" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service1/urun-lite" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service2" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service2/urun" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service2/urun-lite" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service3" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service3/urun" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service3/urun-lite" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service4" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service4/urun" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service4/urun-lite" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service5" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service5/urun" element={<ServicesCards language={language} />} />
      <Route path=":lang/services/service5/urun-lite" element={<ServicesCards language={language} />} />
      <Route path=":lang/career" element={<Career />} />
      <Route path=":lang/career/investors" element={<Career />} />
      <Route path=":lang/career/entrepreneurs" element={<Career />} />
      <Route path=":lang/career/partners" element={<Career />} />
      <Route path=":lang/career/press" element={<Career />} />
      <Route path=":lang/about/company" element={<Company />} />
      <Route path=":lang/about/company/mission" element={<Company />} />
      <Route path=":lang/about/team" element={<Team />} />
      <Route path=":lang/about/team/team" element={<Team />} />
      <Route path=":lang/about/certification" element={<Certification />} />
      <Route path=":lang/about/certification/certificates" element={<Certification />} />
      <Route path=":lang/about/certification/documents" element={<Certification />} />
      <Route path=":lang/about/certification/policy/:policyName" element={<PolicyViewer />} />
      <Route path=":lang/about/invoice" element={<Invoice />} />
      <Route path=":lang/about/invoice/company-info" element={<Invoice />} />
      <Route path=":lang/about/invoice/bank-info" element={<Invoice />} />
      <Route path=":lang/about/certification/cerez" element={<Cerez />} />
      <Route path=":lang/about/certification/gizlilikveri" element={<GizlilikVeri />} />
      <Route path=":lang/about/certification/kvkkaydinlatma" element={<KVKKAydinlatma />} />
      <Route path=":lang/about/certification/verisorumlusubasvuru" element={<VeriSorumlusuBasvuru />} />
      <Route path=":lang/about/certification/gizlilikkullanim" element={<GizlilikKullanim />} />
      <Route path=":lang/about/certification/bilgitoplumu" element={<BilgiToplumu />} />
      <Route path=":lang/about/subsidiaries" element={<Subsidiaries />} />
      <Route path=":lang/about/sustainability" element={<Sustainability />} />
      <Route path=":lang/about/hr" element={<HR />} />
      <Route path=":lang/about/social-responsibility" element={<SocialResponsibility />} />
      <Route path=":lang/about/social-responsibility/:slug" element={<BlogPost />} />
      <Route path=":lang/investments" element={<Investments />} />
      <Route path=":lang/investment/uyumtakip" element={<WebApplications />} />
      <Route path=":lang/investment/uyumtakip/urun" element={<WebApplications />} />
      <Route path=":lang/investment/uyumtakip/urun-lite" element={<WebApplications />} />
      <Route path=":lang/blog" element={<Blog />} />
      <Route path=":lang/blog/:blogId" element={<BlogDetail />} />
      <Route path="*" element={<Navigate to="/tr" replace />} />
    </Routes>
  );
};

export default App;
